import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { PlusIcon, UserIcon, SearchIcon, EditIcon, TrashIcon, CheckIcon, XIcon, MailIcon, PhoneIcon, KeyIcon, UserPlusIcon, FilterIcon } from 'lucide-react';
// Mock data for users
const mockUsers = [{
  id: 1,
  name: 'Erik de Vries',
  email: 'e.devries@vvgn.nl',
  phone: '06-12345678',
  role: 'instructor',
  status: 'active',
  lastLogin: new Date(new Date().setDate(new Date().getDate() - 1))
}, {
  id: 2,
  name: 'Lisa Bakker',
  email: 'l.bakker@vvgn.nl',
  phone: '06-23456789',
  role: 'instructor',
  status: 'active',
  lastLogin: new Date(new Date().setDate(new Date().getDate() - 2))
}, {
  id: 3,
  name: 'Peter Jansen',
  email: 'p.jansen@vvgn.nl',
  phone: '06-34567890',
  role: 'instructor',
  status: 'inactive',
  lastLogin: new Date(new Date().setDate(new Date().getDate() - 30))
}, {
  id: 4,
  name: 'Maria Visser',
  email: 'm.visser@vvgn.nl',
  phone: '06-45678901',
  role: 'planner',
  status: 'active',
  lastLogin: new Date(new Date().setHours(new Date().getHours() - 5))
}, {
  id: 5,
  name: 'Thomas Berg',
  email: 't.berg@vvgn.nl',
  phone: '06-56789012',
  role: 'planner',
  status: 'active',
  lastLogin: new Date(new Date().setHours(new Date().getHours() - 2))
}, {
  id: 6,
  name: 'Joost Smit',
  email: 'j.smit@vvgn.nl',
  phone: '06-67890123',
  role: 'manager',
  status: 'active',
  lastLogin: new Date(new Date().setMinutes(new Date().getMinutes() - 30))
}, {
  id: 7,
  name: 'Anna Bakker',
  email: 'a.bakker@vvgn.nl',
  phone: '06-78901234',
  role: 'manager',
  status: 'active',
  lastLogin: new Date(new Date().setDate(new Date().getDate() - 1))
}];
// Helper function to format date
const formatDate = date => {
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minuten geleden`;
    }
    return `${diffHours} uur geleden`;
  } else if (diffDays === 1) {
    return 'Gisteren';
  } else {
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
};
// Helper function to get role label
const getRoleLabel = role => {
  switch (role) {
    case 'instructor':
      return 'Instructeur';
    case 'planner':
      return 'Planner';
    case 'manager':
      return 'Manager';
    default:
      return 'Onbekend';
  }
};
// Helper function to get role badge variant
const getRoleBadgeVariant = role => {
  switch (role) {
    case 'instructor':
      return 'success';
    case 'planner':
      return 'info';
    case 'manager':
      return 'purple';
    default:
      return 'default';
  }
};
export function UserManagement() {
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Filter users based on search term, role, and status
  const getFilteredUsers = () => {
    return users.filter(user => {
      const matchesSearch = searchTerm === '' || user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === '' || user.role === roleFilter;
      const matchesStatus = statusFilter === '' || user.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  };
  // Open create user modal
  const openCreateUserModal = () => {
    setEditingUser({
      id: null,
      name: '',
      email: '',
      phone: '',
      role: '',
      status: 'active'
    });
    setShowUserModal(true);
  };
  // Open edit user modal
  const openEditUserModal = user => {
    setEditingUser({
      ...user
    });
    setShowUserModal(true);
  };
  // Open delete confirmation
  const openDeleteConfirm = userId => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };
  // Open reset password modal
  const openResetPasswordModal = user => {
    setUserToResetPassword(user);
    setShowResetPasswordModal(true);
  };
  // Handle create/edit user
  const handleCreateOrEditUser = () => {
    if (editingUser.id) {
      // Update existing user
      setUsers(users.map(user => user.id === editingUser.id ? editingUser : user));
      setSuccessMessage('Gebruiker succesvol bijgewerkt');
    } else {
      // Create new user
      const newUser = {
        ...editingUser,
        id: Date.now(),
        lastLogin: null
      };
      setUsers([...users, newUser]);
      setSuccessMessage('Gebruiker succesvol aangemaakt');
    }
    setShowUserModal(false);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Handle delete user
  const handleDeleteUser = () => {
    setUsers(users.filter(user => user.id !== userToDelete));
    setShowDeleteConfirm(false);
    setUserToDelete(null);
    setSuccessMessage('Gebruiker succesvol verwijderd');
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Handle reset password
  const handleResetPassword = () => {
    // In a real app, this would trigger a password reset
    setShowResetPasswordModal(false);
    setUserToResetPassword(null);
    setSuccessMessage('Wachtwoord reset link verstuurd');
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Toggle user status
  const toggleUserStatus = userId => {
    setUsers(users.map(user => {
      if (user.id === userId) {
        const newStatus = user.status === 'active' ? 'inactive' : 'active';
        return {
          ...user,
          status: newStatus
        };
      }
      return user;
    }));
    setSuccessMessage('Gebruikersstatus succesvol gewijzigd');
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Get filtered users
  const filteredUsers = getFilteredUsers();
  // Count users by role
  const userCounts = {
    total: users.length,
    instructor: users.filter(user => user.role === 'instructor').length,
    planner: users.filter(user => user.role === 'planner').length,
    manager: users.filter(user => user.role === 'manager').length,
    active: users.filter(user => user.status === 'active').length,
    inactive: users.filter(user => user.status === 'inactive').length
  };
  return <div className="space-y-6" data-id="element-67">
      <div className="flex justify-between items-center" data-id="element-68">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-69">Gebruikersbeheer</h1>
        <Button variant="primary" size="sm" onClick={openCreateUserModal} data-id="element-70">
          <PlusIcon className="h-4 w-4 mr-1" data-id="element-71" />
          Nieuwe Gebruiker
        </Button>
      </div>

      {/* Success message */}
      {showSuccessMessage && <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 shadow-md flex items-center" data-id="element-72">
          <CheckIcon className="h-5 w-5 mr-2" data-id="element-73" />
          <span data-id="element-74">{successMessage}</span>
        </div>}

      {/* User stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-75">
        <Card className="p-4" data-id="element-76">
          <div className="flex items-center" data-id="element-77">
            <div className="p-3 rounded-full bg-blue-100" data-id="element-78">
              <UserIcon className="h-6 w-6 text-blue-600" data-id="element-79" />
            </div>
            <div className="ml-4" data-id="element-80">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-81">
                Totaal Gebruikers
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-82">
                {userCounts.total}
              </p>
              <p className="text-xs text-gray-500" data-id="element-83">
                {userCounts.active} actief, {userCounts.inactive} inactief
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-84">
          <div className="flex items-center" data-id="element-85">
            <div className="p-3 rounded-full bg-green-100" data-id="element-86">
              <UserIcon className="h-6 w-6 text-green-600" data-id="element-87" />
            </div>
            <div className="ml-4" data-id="element-88">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-89">
                Instructeurs
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-90">
                {userCounts.instructor}
              </p>
              <p className="text-xs text-gray-500" data-id="element-91">
                {users.filter(u => u.role === 'instructor' && u.status === 'active').length}{' '}
                actief
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-92">
          <div className="flex items-center" data-id="element-93">
            <div className="p-3 rounded-full bg-purple-100" data-id="element-94">
              <UserIcon className="h-6 w-6 text-purple-600" data-id="element-95" />
            </div>
            <div className="ml-4" data-id="element-96">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-97">
                Planners & Managers
              </h3>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-98">
                {userCounts.planner + userCounts.manager}
              </p>
              <p className="text-xs text-gray-500" data-id="element-99">
                {userCounts.planner} planners, {userCounts.manager} managers
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0" data-id="element-100">
        <div className="relative w-full sm:w-64" data-id="element-101">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-102">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-103" />
          </div>
          <input type="text" placeholder="Zoek gebruikers..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-104" />
        </div>
        <div className="flex space-x-2" data-id="element-105">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-id="element-106">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-107" />
            Filters {showFilters ? 'verbergen' : 'tonen'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
          setSearchTerm('');
          setRoleFilter('');
          setStatusFilter('');
        }} disabled={!searchTerm && !roleFilter && !statusFilter} data-id="element-108">
            <XIcon className="h-4 w-4 mr-1" data-id="element-109" />
            Reset
          </Button>
        </div>
      </div>

      {/* Expanded filters */}
      {showFilters && <Card className="p-4" data-id="element-110">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-111">
            <div data-id="element-112">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-113">
                Rol
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-114">
                <Button variant={roleFilter === '' ? 'primary' : 'outline'} size="sm" onClick={() => setRoleFilter('')} data-id="element-115">
                  Alle
                </Button>
                <Button variant={roleFilter === 'instructor' ? 'primary' : 'outline'} size="sm" onClick={() => setRoleFilter('instructor')} data-id="element-116">
                  Instructeurs
                </Button>
                <Button variant={roleFilter === 'planner' ? 'primary' : 'outline'} size="sm" onClick={() => setRoleFilter('planner')} data-id="element-117">
                  Planners
                </Button>
                <Button variant={roleFilter === 'manager' ? 'primary' : 'outline'} size="sm" onClick={() => setRoleFilter('manager')} data-id="element-118">
                  Managers
                </Button>
              </div>
            </div>
            <div data-id="element-119">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-120">
                Status
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-121">
                <Button variant={statusFilter === '' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('')} data-id="element-122">
                  Alle
                </Button>
                <Button variant={statusFilter === 'active' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('active')} data-id="element-123">
                  Actief
                </Button>
                <Button variant={statusFilter === 'inactive' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('inactive')} data-id="element-124">
                  Inactief
                </Button>
              </div>
            </div>
          </div>
        </Card>}

      {/* Users list */}
      <Card data-id="element-125">
        <div className="overflow-x-auto" data-id="element-126">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-127">
            <thead className="bg-gray-50" data-id="element-128">
              <tr data-id="element-129">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-130">
                  Naam
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-131">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-132">
                  Rol
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-133">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-134">
                  Laatste Login
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-135">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-136">
              {filteredUsers.length > 0 ? filteredUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-137">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-138">
                      <div className="flex items-center" data-id="element-139">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-140">
                          <UserIcon className="h-5 w-5 text-gray-600" data-id="element-141" />
                        </div>
                        <div className="ml-4" data-id="element-142">
                          <div className="text-sm font-medium text-gray-900" data-id="element-143">
                            {user.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-144">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-145">
                        <MailIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-146" />
                        {user.email}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center" data-id="element-147">
                        <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-148" />
                        {user.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-149">
                      <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-150">
                        {getRoleLabel(user.role)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-151">
                      <div className="flex items-center" data-id="element-152">
                        <div className={`h-2.5 w-2.5 rounded-full mr-2 ${user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-153"></div>
                        <span className="text-sm text-gray-900" data-id="element-154">
                          {user.status === 'active' ? 'Actief' : 'Inactief'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-155">
                      {user.lastLogin ? formatDate(user.lastLogin) : 'Nooit'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-156">
                      <div className="flex justify-end space-x-2" data-id="element-157">
                        <button className="text-indigo-600 hover:text-indigo-900" onClick={() => openEditUserModal(user)} title="Bewerken" data-id="element-158">
                          <EditIcon className="h-4 w-4" data-id="element-159" />
                        </button>
                        <button className="text-yellow-600 hover:text-yellow-900" onClick={() => openResetPasswordModal(user)} title="Reset wachtwoord" data-id="element-160">
                          <KeyIcon className="h-4 w-4" data-id="element-161" />
                        </button>
                        <button className={`${user.status === 'active' ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'}`} onClick={() => toggleUserStatus(user.id)} title={user.status === 'active' ? 'Deactiveren' : 'Activeren'} data-id="element-162">
                          {user.status === 'active' ? <XIcon className="h-4 w-4" data-id="element-163" /> : <CheckIcon className="h-4 w-4" data-id="element-164" />}
                        </button>
                        <button className="text-red-600 hover:text-red-900" onClick={() => openDeleteConfirm(user.id)} title="Verwijderen" data-id="element-165">
                          <TrashIcon className="h-4 w-4" data-id="element-166" />
                        </button>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-167">
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500" data-id="element-168">
                    <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" data-id="element-169" />
                    <p className="text-base" data-id="element-170">Geen gebruikers gevonden</p>
                    <p className="text-sm mt-1" data-id="element-171">
                      Pas je filters aan of maak een nieuwe gebruiker aan
                    </p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit User Modal */}
      {showUserModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-172">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-id="element-173">
            <div className="p-4 border-b border-gray-200" data-id="element-174">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-175">
                {editingUser.id ? 'Gebruiker Bewerken' : 'Nieuwe Gebruiker'}
              </h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-176">
              <div data-id="element-177">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-178">
                  Naam
                </label>
                <input type="text" id="name" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Volledige naam" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-179" />
              </div>
              <div data-id="element-180">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-181">
                  Email
                </label>
                <input type="email" id="email" className="w-full p-2 border border-gray-300 rounded-md" placeholder="email@vvgn.nl" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-182" />
              </div>
              <div data-id="element-183">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-184">
                  Telefoonnummer
                </label>
                <input type="text" id="phone" className="w-full p-2 border border-gray-300 rounded-md" placeholder="06-12345678" value={editingUser.phone} onChange={e => setEditingUser({
              ...editingUser,
              phone: e.target.value
            })} data-id="element-185" />
              </div>
              <div data-id="element-186">
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-187">
                  Rol
                </label>
                <select id="role" className="w-full p-2 border border-gray-300 rounded-md" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-188">
                  <option value="" data-id="element-189">Selecteer een rol</option>
                  <option value="instructor" data-id="element-190">Instructeur</option>
                  <option value="planner" data-id="element-191">Planner</option>
                  <option value="manager" data-id="element-192">Manager</option>
                </select>
              </div>
              <div data-id="element-193">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-194">
                  Status
                </label>
                <select id="status" className="w-full p-2 border border-gray-300 rounded-md" value={editingUser.status} onChange={e => setEditingUser({
              ...editingUser,
              status: e.target.value
            })} data-id="element-195">
                  <option value="active" data-id="element-196">Actief</option>
                  <option value="inactive" data-id="element-197">Inactief</option>
                </select>
              </div>
              {!editingUser.id && <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200" data-id="element-198">
                  <p className="text-sm text-yellow-800" data-id="element-199">
                    Een wachtwoord reset link zal worden verstuurd naar het
                    opgegeven e-mailadres na het aanmaken van de gebruiker.
                  </p>
                </div>}
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-200">
              <Button variant="outline" onClick={() => setShowUserModal(false)} data-id="element-201">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleCreateOrEditUser} disabled={!editingUser.name || !editingUser.email || !editingUser.role} data-id="element-202">
                {editingUser.id ? 'Bijwerken' : 'Aanmaken'}
              </Button>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-203">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-id="element-204">
            <div className="p-4 border-b border-gray-200" data-id="element-205">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-206">
                Gebruiker Verwijderen
              </h3>
            </div>
            <div className="p-4" data-id="element-207">
              <p className="text-gray-600" data-id="element-208">
                Weet je zeker dat je deze gebruiker wilt verwijderen? Deze actie
                kan niet ongedaan worden gemaakt.
              </p>
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-209">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-id="element-210">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700" data-id="element-211">
                <TrashIcon className="h-4 w-4 mr-1" data-id="element-212" />
                Verwijderen
              </Button>
            </div>
          </div>
        </div>}

      {/* Reset Password Modal */}
      {showResetPasswordModal && userToResetPassword && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-213">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-id="element-214">
            <div className="p-4 border-b border-gray-200" data-id="element-215">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-216">
                Wachtwoord Resetten
              </h3>
            </div>
            <div className="p-4" data-id="element-217">
              <p className="text-gray-600" data-id="element-218">
                Je staat op het punt om een wachtwoord reset link te versturen
                naar <strong data-id="element-219">{userToResetPassword.email}</strong>.
              </p>
              <div className="mt-4 bg-blue-50 p-3 rounded-md border border-blue-200" data-id="element-220">
                <p className="text-sm text-blue-800" data-id="element-221">
                  De gebruiker zal een e-mail ontvangen met instructies om een
                  nieuw wachtwoord in te stellen.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-222">
              <Button variant="outline" onClick={() => setShowResetPasswordModal(false)} data-id="element-223">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleResetPassword} data-id="element-224">
                <KeyIcon className="h-4 w-4 mr-1" data-id="element-225" />
                Reset Link Versturen
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}